// src/lib/integrations.ts
//
// Typed API client for the HRIS/payroll integration surfaces (DESIGN_HRISIntegration.md §8.4–8.6).
// Org-scoped endpoints under /api/v1/organizations/integrations, super-admin under
// /api/v1/admin/integrations. Backend: peerpass-api/src/controllers/integrationController.js.

import { api } from './api'

// ── Types ──────────────────────────────────────────────────────────────

export interface IntegrationFieldDef {
  name: string
  type: string
  required?: boolean
  secret?: boolean
  default?: unknown
  format?: string
}

export type IntegrationCategory = 'hris' | 'payroll' | 'hris_payroll'
export type ConnectionStatus = 'configured' | 'active' | 'error' | 'paused' | 'disconnected'
export type SyncStatus = 'success' | 'partial' | 'failed'

export interface IntegrationProvider {
  provider_id: number
  provider_code: string
  display_name: string
  category: IntegrationCategory
  supports_employee_sync: boolean
  supports_bank_info_sync: boolean
  supports_payroll_sync: boolean
  supports_benefits_sync: boolean
  supports_dependents_sync: boolean
  supports_groups_sync: boolean
  supports_writeback: boolean
  connection_config_schema: IntegrationFieldDef[]
  // super-admin only:
  adapter_module?: string
  is_active?: boolean
  sort_order?: number
}

export interface IntegrationConnection {
  connection_id: number
  org_id?: number
  provider_id: number
  label: string | null
  status: ConnectionStatus
  last_sync_at: string | null
  last_sync_status: SyncStatus | null
  last_sync_error: string | null
  last_writeback_at: string | null
  last_writeback_status: SyncStatus | null
  sync_employees: boolean
  sync_bank_info: boolean
  sync_payroll: boolean
  sync_benefits: boolean
  sync_dependents: boolean
  sync_groups: boolean
  enable_writeback: boolean
  employee_import_mode: 'auto' | 'review'
  writeback_approval: 'auto' | 'manual'
  has_config?: boolean
  pending_changes_count?: number
  provider_name?: string
  provider_code?: string
  category?: string
  org_name?: string
  created_at?: string
  updated_at?: string
}

export interface SyncLogEntry {
  log_id: number
  connection_id: number
  sync_type: string
  status: 'started' | 'completed' | 'partial' | 'failed'
  records_total: number | null
  records_imported: number | null
  records_skipped: number | null
  records_failed: number | null
  error_details: unknown
  change_summary: unknown
  started_at: string
  completed_at: string | null
}

export interface PendingChange {
  change_id: number
  connection_id: number
  change_type: 'terminate' | 'bank_update' | 'field_update'
  user_id: number
  change_summary: unknown
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  first_name?: string
  last_name?: string
  email?: string
}

// ── Org-scoped (employer) ─────────────────────────────────────────────

export async function listActiveProvidersApi(): Promise<{ providers: IntegrationProvider[] }> {
  const res = await api.get('/api/v1/organizations/integrations/providers')
  return res.data
}

export async function listOrgConnectionsApi(): Promise<{ connections: IntegrationConnection[] }> {
  const res = await api.get('/api/v1/organizations/integrations/connections')
  return res.data
}

export async function createOrgConnectionApi(params: {
  provider_id: number
  label?: string | null
  config: Record<string, unknown>
  sync_employees?: boolean
  sync_bank_info?: boolean
  sync_payroll?: boolean
  sync_benefits?: boolean
  sync_dependents?: boolean
  sync_groups?: boolean
  enable_writeback?: boolean
  employee_import_mode?: 'auto' | 'review'
  writeback_approval?: 'auto' | 'manual'
}): Promise<{ connection_id: number }> {
  const res = await api.post('/api/v1/organizations/integrations/connections', params)
  return res.data
}

export async function testConnectionApi(
  provider_id: number,
  config: Record<string, unknown>,
): Promise<{ ok: boolean }> {
  const res = await api.post('/api/v1/organizations/integrations/connections/test', { provider_id, config })
  return res.data
}

export async function updateOrgConnectionApi(
  connectionId: number,
  params: Partial<{
    label: string | null
    status: ConnectionStatus
    config: Record<string, unknown>
    sync_employees: boolean
    sync_bank_info: boolean
    sync_payroll: boolean
    sync_benefits: boolean
    sync_dependents: boolean
    sync_groups: boolean
    enable_writeback: boolean
    employee_import_mode: 'auto' | 'review'
    writeback_approval: 'auto' | 'manual'
  }>,
): Promise<{ updated: boolean }> {
  const res = await api.put(`/api/v1/organizations/integrations/connections/${connectionId}`, params)
  return res.data
}

export async function deleteOrgConnectionApi(connectionId: number): Promise<{ deleted: boolean }> {
  const res = await api.delete(`/api/v1/organizations/integrations/connections/${connectionId}`)
  return res.data
}

export async function triggerSyncApi(connectionId: number): Promise<Record<string, unknown>> {
  const res = await api.post(`/api/v1/organizations/integrations/connections/${connectionId}/trigger-sync`)
  return res.data
}

export async function listSyncLogsApi(connectionId: number): Promise<{ sync_logs: SyncLogEntry[] }> {
  const res = await api.get(`/api/v1/organizations/integrations/connections/${connectionId}/sync-logs`)
  return res.data
}

export async function listPendingChangesApi(connectionId: number): Promise<{ pending_changes: PendingChange[] }> {
  const res = await api.get(`/api/v1/organizations/integrations/connections/${connectionId}/pending-changes`)
  return res.data
}

export async function approvePendingChangeApi(
  connectionId: number,
  changeId: number,
): Promise<{ change_id: number; status: string }> {
  const res = await api.post(`/api/v1/organizations/integrations/connections/${connectionId}/pending-changes/${changeId}/approve`)
  return res.data
}

export async function rejectPendingChangeApi(
  connectionId: number,
  changeId: number,
): Promise<{ change_id: number; status: string }> {
  const res = await api.post(`/api/v1/organizations/integrations/connections/${connectionId}/pending-changes/${changeId}/reject`)
  return res.data
}

// ── Super-admin ────────────────────────────────────────────────────────

export async function listAllProvidersApi(): Promise<{ providers: IntegrationProvider[] }> {
  const res = await api.get('/api/v1/admin/integrations/providers')
  return res.data
}

export async function createProviderApi(params: {
  provider_code: string
  display_name: string
  category: IntegrationCategory
  connection_config_schema: IntegrationFieldDef[]
  adapter_module: string
  supports_employee_sync?: boolean
  supports_bank_info_sync?: boolean
  supports_payroll_sync?: boolean
  supports_benefits_sync?: boolean
  supports_dependents_sync?: boolean
  supports_groups_sync?: boolean
  supports_writeback?: boolean
  is_active?: boolean
  sort_order?: number
}): Promise<{ provider_id: number }> {
  const res = await api.post('/api/v1/admin/integrations/providers', params)
  return res.data
}

export async function updateProviderApi(
  providerId: number,
  params: Partial<{
    provider_code: string
    display_name: string
    category: IntegrationCategory
    connection_config_schema: IntegrationFieldDef[]
    adapter_module: string
    supports_employee_sync: boolean
    supports_bank_info_sync: boolean
    supports_payroll_sync: boolean
    supports_benefits_sync: boolean
    supports_dependents_sync: boolean
    supports_groups_sync: boolean
    supports_writeback: boolean
    is_active: boolean
    sort_order: number
  }>,
): Promise<{ updated: boolean }> {
  const res = await api.put(`/api/v1/admin/integrations/providers/${providerId}`, params)
  return res.data
}

export async function listAllConnectionsApi(): Promise<{ connections: IntegrationConnection[] }> {
  const res = await api.get('/api/v1/admin/integrations/connections')
  return res.data
}

export async function overrideConnectionApi(
  connectionId: number,
  params: Partial<{
    status: ConnectionStatus
    enable_writeback: boolean
    employee_import_mode: 'auto' | 'review'
    writeback_approval: 'auto' | 'manual'
  }>,
): Promise<{ updated: boolean }> {
  const res = await api.put(`/api/v1/admin/integrations/connections/${connectionId}`, params)
  return res.data
}